/**
 * AudienceConversationsPage Redux duck.
 *
 * Thin wrapper over the shared conversations factory (see
 * src/ducks/conversations.shared.js). Audience users get a subset of
 * the conversation features (no archive/delete/create — they don't
 * own channels). The shared duck includes those bits but audience
 * components simply don't import them.
 *
 * Audience-specific overrides here:
 *  - fetchSendbirdToken (no custom avatar — uses defaults)
 *  - selectChannelsByType (memberships / episodes / dms; no team)
 *  - loadData
 */

import { CHANNEL_TYPES } from '../../util/sendbird';
import { sendbirdAPI } from '../../util/api';
import { decodeChannelId } from '../../util/channelUrl';
import { createConversationsDuck } from '../../ducks/conversations.shared';

const NAMESPACE = 'AudienceConversationsPage';
const duck = createConversationsDuck(NAMESPACE);

// ============== Re-export shared action types ============== //
export const {
  SET_SENDBIRD_CONNECTED,
  FETCH_TOKEN_REQUEST,
  FETCH_TOKEN_SUCCESS,
  FETCH_TOKEN_ERROR,
  SET_CHANNELS,
  ADD_CHANNELS,
  ADD_CHANNEL,
  UPDATE_CHANNEL,
  UPDATE_CHANNELS,
  REMOVE_CHANNEL,
  REMOVE_CHANNELS,
  SET_HAS_MORE_CHANNELS,
  SET_SELECTED_CHANNEL,
  SET_MESSAGES,
  ADD_MESSAGE,
  ADD_MESSAGES,
  PREPEND_MESSAGES,
  UPDATE_MESSAGE,
  UPDATE_MESSAGES,
  REMOVE_MESSAGE,
  REMOVE_MESSAGES,
  SET_HAS_MORE_MESSAGES,
  SET_LOADING_MORE_MESSAGES,
  SET_THREAD_MESSAGES,
  ADD_THREAD_MESSAGE,
  SET_ACTIVE_THREAD,
  SET_THREAD_LOADING,
  SET_CHANNEL_INFO_OPEN,
  SET_ACTIVE_TAB,
  SET_CHANNEL_MEMBERS,
  SET_PENDING_CHANNEL_URL,
} = duck.types;

// ============== Reducer ============== //
export default duck.reducer;

// ============== Re-export shared action creators ============== //
export const {
  setSendbirdConnected,
  setChannels,
  addChannels,
  addChannel,
  updateChannel,
  updateChannels,
  removeChannel,
  removeChannels,
  setHasMoreChannels,
  setSelectedChannel,
  setMessages,
  addMessage,
  addMessages,
  prependMessages,
  updateMessage,
  updateMessages,
  removeMessage,
  removeMessages,
  setHasMoreMessages,
  setLoadingMoreMessages,
  setThreadMessages,
  addThreadMessage,
  setActiveThread,
  setThreadLoading,
  setChannelInfoOpen,
  setActiveTab,
  setChannelMembers,
  setPendingChannelUrl,
} = duck.actions;

// ============== Re-export shared thunks ============== //
export const { fetchChannelByUrl, createChannel, leaveChannel } = duck.thunks;

// Gig Boost Dual Opt-In — only audience members are ever eligible, so the
// thunks are surfaced on this page's duck.
export const { fetchDualOptInStatus, submitDualOptInDecision } = duck.thunks;

// Episode RSVPs — "Reserve my spot" on an episode card in the thread.
export const { fetchEpisodeRsvps, toggleEpisodeRsvp } = duck.thunks;

// ============== Re-export shared selectors ============== //
export const {
  selectSendbirdConnected,
  selectTokenLoading,
  selectTokenError,
  selectSendbirdToken,
  selectChannels,
  selectHasMoreChannels,
  selectSelectedChannel,
  selectMessages,
  selectMessagesLoading,
  selectHasMoreMessages,
  selectLoadingMoreMessages,
  selectActiveTab,
  selectChannelInfoOpen,
  selectActiveThreadMessageId,
  selectThreadMessages,
  selectThreadLoading,
  selectChannelMembers,
  selectPendingChannelUrl,
  selectTotalUnreadCount,
  selectDualOptIn,
  selectDualOptInSubmitting,
  selectEpisodeRsvps,
} = duck.selectors;

// ============== Audience-specific thunks ============== //

export const fetchSendbirdToken = () => async dispatch => {
  dispatch({ type: FETCH_TOKEN_REQUEST });
  try {
    const response = await sendbirdAPI.getToken();
    dispatch({ type: FETCH_TOKEN_SUCCESS, payload: response });
    return response;
  } catch (error) {
    console.error('Fetch token error:', error);
    dispatch({ type: FETCH_TOKEN_ERROR, payload: error.message });
    throw error;
  }
};

// ============== Audience-specific selectors ============== //

/**
 * Audience grouping: LIVE_ROOM channels become "My Memberships",
 * EPISODE channels become "My Episodes", DMs stay as messages.
 * No team bucket — audience users aren't on any team.
 */
export const selectChannelsByType = state => {
  const channels = selectChannels(state) || [];
  const grouped = { memberships: [], episodes: [], dms: [] };
  channels.forEach(channel => {
    switch (channel.customType) {
      case CHANNEL_TYPES.LIVE_ROOM:
        grouped.memberships.push(channel);
        break;
      case CHANNEL_TYPES.EPISODE:
        grouped.episodes.push(channel);
        break;
      case CHANNEL_TYPES.DM:
        grouped.dms.push(channel);
        break;
      default:
        break;
    }
  });
  return grouped;
};

// ============== loadData ============== //

export const loadData = params => async dispatch => {
  const { channelId } = params;
  if (channelId) {
    dispatch(setPendingChannelUrl(decodeChannelId(channelId)));
  } else {
    dispatch(setPendingChannelUrl(null));
  }
  return dispatch(fetchSendbirdToken());
};
